import gql from 'graphql-tag';
import React from 'react';
import { useQuery } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { format } from 'date-fns';
import { Navigation, LayoutContent, Layout } from '../../components';
import { TopBarDatePicker } from '../../components/TopBarDatePicker';
import { useStartAndEnd, useQueryParams } from '../../hooks';
import { resetTimeToLastMinuteOfDay, resetTimeToMidnight, toISOString } from '../../utils/date';
import {
  DisbursementsListQuery,
  DisbursementsListQuery_disbursements,
  DisbursementsListQueryVariables,
} from './__generated__/DisbursementsListQuery';
import { ReportTable } from './components/ReportTable';
import { flipDirection, SortDirection } from './utils';

const queryStringBuilder = (params) =>
  Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

const headers = ['Date', 'Disbursement', 'Sales', 'Service Fees', 'Processing'];

const orderByColumns = ['date', 'total', 'sales', 'fees', 'processing'];

const moneyFormat = (num) => `$${num.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,')}`;
const columnValuesFromItem = (item) => {
  const date = new Date(item.disbursementDate);
  return [
    format(date, 'MMM. d, yyyy'),
    moneyFormat(item.amount),
    moneyFormat(item.sales),
    `${moneyFormat(item.convenienceFees)}`,
    `-${moneyFormat(item.processingFees)}`,
  ];
};

const DISBURSEMENT_QUERY = gql`
  query DisbursementsListQuery(
    $venueId: ID!
    $start: String!
    $end: String!
    $page: Int!
    $orderBy: String!
    $direction: String!
  ) {
    disbursements(venueId: $venueId, start: $start, end: $end, page: $page, orderBy: $orderBy, direction: $direction) {
      id
      amount
      disbursementDate
      processingFees
      sales
      convenienceFees
    }
  }
`;

interface DisbursementRouterParams {
  venueId: string;
}

type DisbursementsRouterProps = RouteComponentProps<DisbursementRouterParams>;

interface DisbursementsProps extends DisbursementsRouterProps {
  start: Date;
  end: Date;
  page: number;
  orderBy: string;
  direction: SortDirection;
  error?: Error;
  loading: boolean;
  disbursements: DisbursementsListQuery_disbursements[];
}

interface DisbursementsState {
  checked: string[];
}

class DisbursementsUnwrapped extends React.Component<DisbursementsProps, DisbursementsState> {
  constructor(props) {
    super(props);

    this.state = { checked: [] };
  }

  public updateUrl = ({ start, end, page, orderBy, direction }) => {
    const startISO = toISOString(resetTimeToMidnight(start));
    const endISO = toISOString(resetTimeToLastMinuteOfDay(end));

    const suffix = queryStringBuilder({
      page: page || 1,
      start: startISO,
      end: endISO,
      orderBy: orderBy || 'date',
      direction: direction || 'desc',
    });

    this.props.history.push(`/venue/${this.props.match.params.venueId}/reports/disbursements?${suffix}`);
  };

  public goToNextPage = () => {
    const { start, end, page, orderBy, direction } = this.props;
    this.updateUrl({ start, end, direction, orderBy, page: Number(page) + 1 });
  };

  public goToPrevPage = () => {
    const { start, end, page, orderBy, direction } = this.props;
    this.updateUrl({ start, end, direction, orderBy, page: Number(page) - 1 });
  };

  public onChangeDate = (start, end) => {
    const { page, orderBy, direction } = this.props;
    this.updateUrl({ start, end, direction, orderBy, page });
  };

  public onClickHeaderCell = (index) => {
    const { start, end, page, orderBy, direction } = this.props;
    if (orderByColumns[index] === orderBy) {
      this.updateUrl({
        start,
        end,
        direction: flipDirection(direction),
        orderBy,
        page,
      });
    } else {
      this.updateUrl({
        start,
        end,
        direction,
        orderBy: orderByColumns[index],
        page,
      });
    }
  };

  public onPressCheckbox = (disbursement) => {
    if (this.state.checked.includes(disbursement.id)) {
      this.setState({
        checked: this.state.checked.filter((id) => id !== disbursement.id),
      });
    } else {
      this.setState({
        checked: this.state.checked.concat(disbursement.id),
      });
    }
  };

  public onChangeSection = () => {
    const { start, end } = this.props;
    const { venueId } = this.props.match.params;
    this.props.history.push(`/venue/${venueId}/reports/sessions?start=${start}&end=${end}`);
  };

  public fetchCheckedReport = () => {
    this.exportReport(this.state.checked);
  };

  public exportReport = async (ids: string[]) => {
    if (ids.length === 0) {
      return;
    }

    const link = document.createElement('a');
    document.body.appendChild(link);

    link.href = `/venues/${this.props.match.params.venueId}/disbursements/${ids}`;
    link.download = 'Report.xlsx';
    link.click();
  };

  public onCheckAll = () => {
    if (this.state.checked.length === 0) {
      const checked = this.props.disbursements.map((d) => d.id);
      this.setState({ checked });
    } else {
      this.setState({ checked: [] });
    }
  };

  public renderFooter = () => {
    const totals = this.props.disbursements.reduce(
      (accum, disbursement) => {
        accum.total += disbursement.amount;
        accum.salesTotal += disbursement.sales;
        accum.processingTotal += disbursement.processingFees;
        accum.feeTotal += disbursement.convenienceFees;
        return accum;
      },
      {
        total: 0,
        salesTotal: 0,
        processingTotal: 0,
        feeTotal: 0,
      }
    );

    return [
      <td colSpan={2} key='total'>
        Total
      </td>,
      <td key='disbursement-total'>{moneyFormat(totals.total)}</td>,
      <td key='sales-total'>{moneyFormat(totals.salesTotal)}</td>,
      <td key='fee-total'>{moneyFormat(totals.feeTotal)}</td>,
      <td key='processing-total'>-{moneyFormat(totals.processingTotal)}</td>,
    ];
  };

  render() {
    const { start, end } = this.props;

    const orderByHeaderIndex = orderByColumns.indexOf(this.props.orderBy);

    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            <Layout.TopBar.Title>Venue Reports</Layout.TopBar.Title>
          </Layout.TopBar.Left>
          <Layout.TopBar.Right>
            <TopBarDatePicker start={start} end={end} onSubmit={this.onChangeDate} />
          </Layout.TopBar.Right>
        </Layout.TopBar>
        <LayoutContent error={this.props.error}>
          <ReportTable
            showDisbursementOption={true}
            headers={headers}
            onCheckAll={this.onCheckAll}
            onPressCheckbox={this.onPressCheckbox}
            checked={this.state.checked}
            loading={this.props.loading}
            rows={this.props.disbursements}
            fetchReport={this.exportReport}
            fetchCheckedReport={this.fetchCheckedReport}
            columnValuesFromItem={columnValuesFromItem}
            onChangeSection={this.onChangeSection}
            reportType={'disbursements'}
            page={this.props.page}
            maxPage={99}
            goToPrevPage={this.goToPrevPage}
            goToNextPage={this.goToNextPage}
            FooterComponent={this.renderFooter}
            orderByHeaderIndex={orderByHeaderIndex}
            orderByDirection={this.props.direction}
            onClickHeaderCell={this.onClickHeaderCell}
          />
        </LayoutContent>
      </Layout>
    );
  }
}

export const Disbursements: React.FC<DisbursementsRouterProps> = (props) => {
  const { start, end } = useStartAndEnd();
  const queryParams = useQueryParams();

  const page = queryParams.get('page') ? Number(queryParams.get('page')) : 1;
  const orderBy = queryParams.get('orderBy') || 'date';
  const direction = (queryParams.get('direction') || 'desc') as SortDirection;

  const { data, loading, error } = useQuery<DisbursementsListQuery, DisbursementsListQueryVariables>(
    DISBURSEMENT_QUERY,
    {
      variables: {
        venueId: props.match.params.venueId,
        start: start.toISOString(),
        end: end.toISOString(),
        page,
        orderBy,
        direction,
      },
    }
  );

  return (
    <DisbursementsUnwrapped
      {...props}
      loading={loading}
      error={error}
      start={start}
      end={end}
      page={page}
      orderBy={orderBy}
      direction={direction}
      disbursements={data?.disbursements}
    />
  );
};
